// Admin finance: plan catalogue, tax rates, Paystack disputes, reconciliation
// and the payment webhook log — `/api/admin/v2/billing/*` on ff-api.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { APIResponse } from '@/client/types/api';
import { Pagination } from '@/client/types/payment';

const B = '/api/admin/v2/billing';
const TIMEOUT = { timeout: 45_000 };

const unwrap = async <T,>(promise: Promise<APIResponse<T> | T>): Promise<T> => {
  const res = await promise;
  if (res != null && typeof res === 'object' && 'data' in (res as object) && 'status' in (res as object)) {
    return (res as APIResponse<T>).data as T;
  }
  return res as T;
};

const qs = (raw: object) => {
  const p = new URLSearchParams();
  Object.entries(raw).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const financeKeys = {
  all: ['billing-finance'] as const,
  catalog: (p: object) => [...financeKeys.all, 'catalog', p] as const,
  taxes: () => [...financeKeys.all, 'taxes'] as const,
  disputes: (p: object) => [...financeKeys.all, 'disputes', p] as const,
  reconciliation: (p: object) => [...financeKeys.all, 'reconciliation', p] as const,
  webhooks: (p: object) => [...financeKeys.all, 'webhooks', p] as const,
  webhook: (id: string) => [...financeKeys.all, 'webhook', id] as const,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CatalogPrice {
  pricid: number;
  ccode: string;
  /** Pre-tax, as stored. */
  price: number | null;
  /** What the buyer is billed. */
  price_incl_tax: number | null;
  price_old: number | null;
  date_modified: string | null;
  /** Set when the stored price already breaks the catalogue convention. */
  rule_violation: string | null;
}

export interface CatalogPlan {
  toid: number;
  ffid: string | null;
  name: string | null;
  collection: string | null;
  tier: number | null;
  sub_duration: number | null;
  active: number;
  popular: number;
  featured: number;
  /** Cost of the visits this plan dispatches; 0 for software-only plans. */
  visit_cost: number;
  /** Lowest pre-tax price the visit rule allows, when the plan has visits. */
  price_floor: number | null;
  prices: CatalogPrice[];
}

export interface TaxRow {
  taxid: number;
  name: string | null;
  country_iso2: string | null;
  rate: number | null;
  rate_description: string | null;
  computed: boolean;
}

export interface Dispute {
  id: number;
  status?: string;
  resolution?: string | null;
  category?: string;
  refund_amount?: number | null;
  currency?: string;
  dueAt?: string | null;
  createdAt?: string | null;
  resolvedAt?: string | null;
  transaction?: { reference?: string; amount?: number; currency?: string } | null;
  customer?: { email?: string } | null;
  payment?: { pid: number; uid: number | null; oid: number | null; invid: number | null } | null;
}

export interface GatewayCharge {
  reference: string;
  amount: number;
  ccode: string;
  paid_at?: string | null;
  channel?: string | null;
  email?: string | null;
  gateway_id?: number;
  pid?: number;
  local_amount?: number;
}

export interface Reconciliation {
  mode: 'live' | 'test';
  days: number;
  gateway_count: number;
  gateway_total: number;
  matched: number;
  truncated: boolean;
  gateway_only: GatewayCharge[];
  amount_mismatch: GatewayCharge[];
  local_only: Array<{
    pid: number;
    reference: string;
    amount: number;
    ccode: string;
    uid: number | null;
    invid: number | null;
    date_added: string | null;
  }>;
  balance: Array<{ ccode: string; balance: number }>;
}

export interface WebhookEvent {
  id: string;
  created_at: string | null;
  processor: string;
  event: string | null;
  reference: string | null;
  mode: string | null;
  status_code: number;
  accepted: boolean;
  trace_id: string | null;
  size: number;
  payload?: unknown;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

const useGet = <T,>(key: readonly unknown[], path: string, message: string, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: key,
    queryFn: withErrorHandling(async (): Promise<T> => unwrap(request<APIResponse<T>>(path)), message),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useCatalogQuery = (p: { active?: 0 | 1; q?: string } = {}) =>
  useGet<{ plans: CatalogPlan[]; count: number }>(
    financeKeys.catalog(p), `${B}/catalog${qs(p)}`, 'Failed to fetch the plan catalogue');

export const useTaxesQuery = () =>
  useGet<{ taxes: TaxRow[] }>(financeKeys.taxes(), `${B}/taxes`, 'Failed to fetch taxes');

export const useDisputesQuery = (p: { status?: string; page?: number }) =>
  useGet<{ disputes: Dispute[]; mode: string }>(
    financeKeys.disputes(p), `${B}/disputes${qs(p)}`, 'Failed to fetch disputes');

export const useReconciliationQuery = (p: { days: number }, enabled = true) =>
  useGet<Reconciliation>(
    financeKeys.reconciliation(p), `${B}/reconciliation${qs(p)}`, 'Failed to reconcile', enabled);

export const useWebhookEventsQuery = (p: {
  processor?: string;
  event?: string;
  reference?: string;
  accepted?: 0 | 1;
  limit?: number;
  offset?: number;
}) =>
  useGet<{ events: WebhookEvent[]; pagination: Pagination }>(
    financeKeys.webhooks(p), `${B}/webhooks${qs(p)}`, 'Failed to fetch webhook events');

export const useWebhookEventQuery = (id: string | null) =>
  useGet<WebhookEvent>(
    financeKeys.webhook(id ?? ''), `${B}/webhooks/${id}`, 'Failed to fetch the event', !!id);

// ─── Mutations ────────────────────────────────────────────────────────────────

/** One write to `${B}${path}`; refreshes every finance query on success. */
const useWrite = <A,>(
  build: (args: A) => { path: string; method: 'POST' | 'PUT'; body: object },
  message: string,
) => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: A) => {
    const { path, method, body } = build(args);
    return unwrap(request<any>(`${B}${path}`, method, body, {
      ...TIMEOUT,
      idempotencyKey: generateIdempotencyKey(),
    }));
  }, message);
  return useMutation({
    mutationFn: (args: A) => fn(args),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.all }),
  });
};

export const useSetPlanPriceMutation = () =>
  useWrite<{ toid: number; price: number; reason: string; ccode?: string }>(
    ({ toid, ...body }) => ({ path: `/catalog/${toid}/price`, method: 'PUT', body }),
    'Failed to change the price');

export const useSetPlanActiveMutation = () =>
  useWrite<{ toid: number; active: 0 | 1; reason: string }>(
    ({ toid, ...body }) => ({ path: `/catalog/${toid}/active`, method: 'PUT', body }),
    'Failed to update the plan');

export const useSetTaxRateMutation = () =>
  useWrite<{ taxid: number; rate: number; reason: string }>(
    ({ taxid, ...body }) => ({ path: `/taxes/${taxid}`, method: 'PUT', body }),
    'Failed to change the tax rate');

export const useResolveDisputeMutation = () =>
  useWrite<{
    id: number;
    resolution: 'merchant-accepted' | 'declined';
    message: string;
    refund_amount?: number;
  }>(
    ({ id, ...body }) => ({ path: `/disputes/${id}/resolve`, method: 'POST', body }),
    'Failed to resolve the dispute');

export const useReplayWebhookMutation = () =>
  useWrite<{ id: string }>(
    ({ id }) => ({ path: `/webhooks/${id}/replay`, method: 'POST', body: {} }),
    'Failed to replay the event');
